/**
 * One question on the BD questionnaire: the card, its three states, and the timeline dot.
 *
 * ## The three states are one component, not three
 *
 * From the design: a question is `upcoming` (title only, hollow dot), `current` (expanded with
 * its answer control and a Next button, filled dot) or `answered` (title plus the answer as
 * text, tick dot). They share the title row, the `*NOTES` link and the dot, and the dot's
 * state has to agree with the body's — so splitting them into three components would mean
 * three places to keep that agreement.
 *
 * ## Why the answer control is a child rather than a prop switch
 *
 * Each question kind needs a different control, and they have genuinely different shapes: a
 * multi-select has to police which options may be combined, the number input carries a unit,
 * the SFDC one calls a service before it will let you move on. Encoding that as a `kind` prop
 * on this component would put five unrelated concerns in one file; it takes the rendered
 * control instead, and `AnswerBody` does the switch in one place.
 */
import type { ReactNode } from 'react';
import './QuestionCard.css';

export type QuestionCardState = 'upcoming' | 'current' | 'answered';

export interface QuestionCardProps {
  /** 1-based, as shown in the dot. */
  ordinal: number;
  state: QuestionCardState;
  /** English prompt. Always present. */
  prompt: string;
  /**
   * Chinese prompt, shown UNDER the English one rather than instead of it.
   *
   * The design shows both at once, which is not the same as a language toggle: a BD reading
   * the Chinese still needs the English program names the answer options carry. Three of the
   * five live trees have empty `cn`, so this is often absent — see the placeholder note in
   * `QuestionnaireMockPage`.
   */
  promptZh?: string;
  /** The answer control, for `current`. */
  children?: ReactNode;
  /** What the BD chose, for `answered`. One line of text. */
  answerSummary?: string;
  /** Opens the note in the chat panel. Absent when the question has no note. */
  onOpenNotes?: () => void;
  /** Hidden on the last question of a path; the flow decides, not this component. */
  onNext?: () => void;
  nextDisabled?: boolean;
  /**
   * A check is running — the control's own validation, not a page load.
   *
   * Separate from `nextDisabled` because the reasons differ and so does the remedy: disabled
   * means "answer the question", busy means "wait". One flag for both would make a round trip
   * look like an unanswered question.
   */
  nextBusy?: boolean;
  nextLabel?: string;
  /** Last card in the list: the timeline must not continue past it. */
  isLast?: boolean;
  /**
   * This card is the walk's GUESS at what comes next, not a commitment.
   *
   * A decision tree cannot know question 4 before question 3 is answered, so the preview
   * takes the first branch at each step. Marking it is the difference between a plan and a
   * prediction -- and an unmarked prediction is how a progress indicator ends up going
   * backwards.
   */
  speculative?: boolean;
  /** Re-open an answered question. */
  onReopen?: () => void;
  /**
   * Clicking the timeline dot. Same effect as `onReopen`, reachable from the rail.
   *
   * The rail is what a reader scans to find a question they answered earlier; the answer text
   * is only findable once you are already on the right card. Absent for the open and upcoming
   * states, where there is nothing to go back to.
   */
  onSelectDot?: () => void;
}

export default function QuestionCard({
  ordinal,
  state,
  prompt,
  promptZh,
  children,
  answerSummary,
  onOpenNotes,
  onNext,
  nextDisabled,
  nextBusy,
  nextLabel = 'Next',
  isLast,
  speculative,
  onReopen,
  onSelectDot,
}: QuestionCardProps) {
  return (
    <div className={`cpa-q cpa-q--${state}${speculative ? ' cpa-q--speculative' : ''}`}>
      {/*
        The timeline. The connector is drawn by the dot's own ::after rather than as a
        separate element behind the list, because the cards differ in height — a single
        absolutely-positioned line would have to be measured, and would drift the moment a
        card expands.
      */}
      <div className="cpa-q__rail">
        {/*
          A button only when it does something. A dot that looks interactive on the open or an
          upcoming question and then does nothing is worse than a plain one, so the element
          itself changes rather than just the cursor.
        */}
        {onSelectDot ? (
          <button
            type="button"
            className={`cpa-q__dot cpa-q__dot--${state} cpa-q__dot--clickable`}
            onClick={onSelectDot}
            title={`回到第 ${ordinal} 题`}
            aria-label={`回到第 ${ordinal} 题`}
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path
                d="M3.5 8.5l3 3 6-6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : (
          <div className={`cpa-q__dot cpa-q__dot--${state}`} aria-hidden="true">
            {state === 'answered' ? (
              <svg viewBox="0 0 16 16" width="16" height="16">
                <path
                  d="M3.5 8.5l3 3 6-6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              ordinal
            )}
          </div>
        )}
        {!isLast && <div className="cpa-q__line" aria-hidden="true" />}
      </div>

      <div className="cpa-q__card">
        <div className="cpa-q__head">
          <div className="cpa-q__prompt">
            <div className="cpa-q__prompt-en">{prompt}</div>
            {/*
              The Chinese line shows ONLY on the open question.
              In the design an upcoming card is one line and an answered card is the question
              plus its answer -- neither carries the translation. Showing it on all three
              would double the height of a list that is mostly closed cards, and the
              translation is help for answering, which is over once the answer is given.
              27 of the 30 live questions have one.
            */}
            {promptZh && state === 'current' && <div className="cpa-q__prompt-zh">{promptZh}</div>}
          </div>
          {onOpenNotes && (
            <button type="button" className="cpa-q__notes" onClick={onOpenNotes}>
              *NOTES
            </button>
          )}
        </div>

        {state === 'current' && (
          <div className="cpa-q__body">
            {children}
            {onNext && (
              <button
                type="button"
                className="cpa-q__next"
                /* Busy also disables: the check is already running, and a second press would
                   start another round trip against the same value. */
                disabled={nextDisabled || nextBusy}
                onClick={onNext}
              >
                {nextBusy ? '校验中…' : nextLabel}
              </button>
            )}
          </div>
        )}

        {state === 'answered' && (
          <div className="cpa-q__answer">
            {/*
              The answer stays visible and clickable. A decision tree makes going back
              consequential — changing an earlier answer can invalidate every question after
              it — so hiding the answer behind an edit affordance would hide the thing the BD
              needs in order to decide whether to touch it.
            */}
            {onReopen ? (
              <button type="button" className="cpa-q__answer-text" onClick={onReopen}>
                {answerSummary}
              </button>
            ) : (
              <span className="cpa-q__answer-text">{answerSummary}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
